/**
 * Auth Store
 * Manages user authentication state with session caching
 */
import { create } from 'zustand';
import { resetQueryClientForAuthChange } from '@/feat/api-service-layer/queryClient';
import { useVesselStore } from '@/stores/slices/useVesselStore';
import { nowTimestamp } from '@/utils/date';

const SESSION_CACHE_KEY = 'seawatch_session';
const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes

interface User {
  idToken: {
    jwtToken: string;
    payload: {
      name: string;
      [key: string]: unknown;
    };
  };
  config?: unknown;
  [key: string]: unknown;
}

interface CachedSession {
  user: User;
  expiresAt: number;
  lastActivity: number;
}

interface AuthStore {
  user: User | null;
  loading: boolean;
  isInitializing: boolean;
  lastActivity: number;
  getToken: (() => Promise<string>) | null;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setIsInitializing: (isInitializing: boolean) => void;
  updateActivity: () => void;
  setGetToken: (getToken: () => Promise<string>) => void;

  // Session caching methods
  getCachedSession: () => CachedSession | null;
  saveSessionToCache: (user: User) => void;
  clearSessionCache: () => void;
  isSessionCacheValid: () => boolean;
  extendSessionExpiration: () => void;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  loading: false,
  isInitializing: true, // Start as initializing
  lastActivity: nowTimestamp(),
  getToken: null,

  setUser: (user) => {
    // Whenever auth state changes (login, logout, user switch),
    // reset React Query cache and any vessel-related state so
    // we never reuse stale data from a previous session.
    resetQueryClientForAuthChange();

    // Clear vessel store so sidebars and map start from a clean slate
    try {
      const vesselState = useVesselStore.getState();
      vesselState.setVessels([]);
      vesselState.setSelectedPolygon(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to reset vessel store on auth change:', error);
    }

    set({ user });
    if (user) {
      get().saveSessionToCache(user);
    } else {
      get().clearSessionCache();
    }
  },
  setLoading: (loading) => set({ loading }),
  setIsInitializing: (isInitializing) => set({ isInitializing }),
  updateActivity: () => {
    const now = nowTimestamp();
    set({ lastActivity: now });
    // Extend session expiration on activity
    get().extendSessionExpiration();
  },
  setGetToken: (getToken) => set({ getToken }),

  // Session caching methods
  getCachedSession: () => {
    try {
      const cached = localStorage.getItem(SESSION_CACHE_KEY);
      if (!cached) return null;
      return JSON.parse(cached) as CachedSession;
    } catch (error) {
      console.error('Error reading session cache:', error);
      return null;
    }
  },

  saveSessionToCache: (user: User) => {
    try {
      const now = nowTimestamp();
      const cachedSession: CachedSession = {
        user,
        expiresAt: now + SESSION_DURATION,
        lastActivity: now,
      };
      localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cachedSession));
      set({ lastActivity: now });
    } catch (error) {
      console.error('Error saving session cache:', error);
    }
  },

  clearSessionCache: () => {
    try {
      localStorage.removeItem(SESSION_CACHE_KEY);
    } catch (error) {
      console.error('Error clearing session cache:', error);
    }
  },

  isSessionCacheValid: () => {
    const cached = get().getCachedSession();
    if (!cached) return false;
    const now = nowTimestamp();
    // Check if session is expired
    if (now >= cached.expiresAt) {
      get().clearSessionCache();
      return false;
    }
    // Check if session is idle (no activity for SESSION_DURATION)
    if (now - cached.lastActivity >= SESSION_DURATION) {
      get().clearSessionCache();
      return false;
    }
    return true;
  },

  extendSessionExpiration: () => {
    const cached = get().getCachedSession();
    if (!cached) return;
    const now = nowTimestamp();
    // Only extend if within valid session window
    if (now < cached.expiresAt && now - cached.lastActivity < SESSION_DURATION) {
      const newExpiresAt = Math.min(now + SESSION_DURATION, cached.expiresAt + SESSION_DURATION);
      const updatedCache: CachedSession = {
        ...cached,
        expiresAt: newExpiresAt,
        lastActivity: now,
      };
      try {
        localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(updatedCache));
      } catch (error) {
        console.error('Error extending session cache:', error);
      }
    }
  },
}));
